import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { mlmAPI } from "../services/api";

const MyDirect = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDirectReferrals();
  }, []);

  const fetchDirectReferrals = async () => {
    try {
      setLoading(true);
      const data = await mlmAPI.getDirectReferrals();
      const normalized = (data || []).map((referral) => ({
        ...referral,
        totalEarnings: Number(referral.totalEarnings || 0),
        status:
          referral.status ||
          (referral.isActive === false ? "Inactive" : "Active"),
      }));
      setReferrals(normalized);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch direct referrals:", err);
      console.error("Error response:", err.response);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to load referrals. Please make sure you are logged in.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-500/20 text-green-400 border border-green-500/30";
      case "Pending":
        return "bg-amber-500/20 text-amber-400 border border-amber-500/30";
      case "Inactive":
        return "bg-red-500/20 text-red-400 border border-red-500/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-500/30";
    }
  };

  const filteredReferrals = referrals.filter((referral) => {
    const matchesSearch = referral.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || referral.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: referrals.length,
    active: referrals.filter((r) => r.status === "Active").length,
    totalEarnings: referrals.reduce(
      (sum, r) => sum + (r.totalEarnings || 0),
      0,
    ),
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-8 md:ml-64">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-surface-soft rounded w-1/4 mb-4"></div>
              <div className="h-64 bg-surface-soft rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-8 md:ml-64">
          <div className="max-w-7xl mx-auto">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={fetchDirectReferrals}
                  className="px-6 py-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold rounded-lg"
                >
                  Retry
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const handleView = (referral) => {
    console.log("Viewing referral:", referral.name);
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Direct Referrals
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Track your direct referral network
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary transition-all"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] focus:outline-none focus:border-primary transition-all"
            >
              <option value="All" className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                Filter by Status: All
              </option>
              <option value="Active" className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                Active
              </option>
              <option value="Pending" className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                Pending
              </option>
              <option value="Inactive" className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                Inactive
              </option>
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-primary)] opacity-60">Total Referrals</p>
              <p className="text-3xl font-bold text-[var(--text-primary)]">{stats.total}</p>
            </div>
          </Card>
          <Card>
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-primary)] opacity-60">Active Members</p>
              <p className="text-3xl font-bold text-green-400">
                {stats.active}
              </p>
            </div>
          </Card>
          <Card>
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-primary)] opacity-60">Total Earnings</p>
              <p className="text-3xl font-bold text-[var(--text-primary)]">
                ${stats.totalEarnings.toFixed(2)}
              </p>
            </div>
          </Card>
          <Card>
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-primary)] opacity-60">Showing</p>
              <p className="text-3xl font-bold text-green-400">
                {filteredReferrals.length}
              </p>
            </div>
          </Card>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary/10">
                    <th className="px-6 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                      Referral Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                      Join Date
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-[var(--text-primary)]">
                      Total Earnings
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-[var(--text-primary)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferrals.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center">
                        <p className="text-[var(--text-primary)] opacity-60">
                          No referrals found
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredReferrals.map((referral) => (
                      <tr
                        key={referral.id}
                        className="border-b border-primary/5 hover:bg-[#22c55e]/5 transition-all duration-200"
                      >
                        {/* Referral Name with Avatar */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] flex items-center justify-center text-white font-bold text-sm">
                              {referral.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase() || "?"}
                            </div>
                            <div>
                              <span className="text-sm text-[var(--text-primary)] font-semibold block">
                                {referral.name}
                              </span>
                              <span className="text-xs text-[var(--text-primary)] opacity-60">
                                {referral.email}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Join Date */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-[var(--text-primary)]">
                            {new Date(referral.createdAt).toLocaleDateString()}
                          </span>
                        </td>

                        {/* Status Badge */}
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                              referral.status || "Active",
                            )}`}
                          >
                            {referral.status || "Active"}
                          </span>
                        </td>

                        {/* Total Earnings */}
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-bold text-[var(--text-primary)]">
                            ${(referral.totalEarnings || 0).toFixed(2)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() =>
                                navigate(`/team-member-details/${referral.id}`)
                              }
                              className="p-2 rounded-lg bg-[var(--bg-card)] border border-primary/20 hover:border-primary transition-all"
                            >
                              <svg
                                className="w-4 h-4 text-[var(--text-primary)]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {filteredReferrals.map((referral) => (
            <Card
              key={referral.id}
              className="hover:transform hover:scale-[1.02] transition-all"
            >
              <div className="space-y-4">
                {/* Header Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] flex items-center justify-center text-white font-bold">
                      {referral.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="text-base text-[var(--text-primary)] font-bold">
                        {referral.name}
                      </p>
                      <p className="text-xs text-[var(--text-primary)] opacity-60">
                        {new Date(referral.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                      referral.status,
                    )}`}
                  >
                    {referral.status}
                  </span>
                </div>

                {/* Earnings */}
                <div className="p-3 bg-[var(--bg-card)] rounded-xl border border-primary/10">
                  <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">
                    Total Earnings
                  </p>
                  <p className="text-2xl text-[var(--text-primary)] font-bold">
                    $
                    {referral.totalEarnings.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      navigate(`/team-member-details/${referral.id}`)
                    }
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold rounded-xl hover:opacity-90 transition-all"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View Details
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center py-6 mt-8">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyDirect;
